// 📁 src/lightversion/LiteDayLayout.jsx

import React, { useEffect } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Button,
  Stack,
  Chip,
} from "@mui/material";
import { motion } from "framer-motion";
import { useLite } from "./context/LiteContext";
import { useAuth } from "../context/AuthContext";
import { useNavigate } from "react-router-dom";

/* ======================================================
   DAY META — SINGLE SOURCE OF TRUTH
====================================================== */
const DAY_META = {
  1: {
    title: "Discover What Truly Matters",
    focus: "Reflect on problems that emotionally stay with you",
    guided: [
      "Why most startup ideas fail before they start",
      "Difference between interest and obsession",
      "How innovators spot meaningful problems",
    ],
  },
  2: {
    title: "Clarify Your Purpose",
    focus: "Turn reflection into a clear purpose statement",
    guided: [
      "Good vs bad purpose statements",
      "Avoiding solution-first thinking",
      "Purpose clarity examples",
    ],
  },
  3: {
    title: "Explore Directions",
    focus: "Explore multiple ways the problem could be addressed",
    guided: [
      "Why first ideas are usually wrong",
      "Thinking in directions, not startups",
      "Avoiding premature commitment",
    ],
  },
  4: {
    title: "Align Emotionally",
    focus: "Ensure the direction resonates deeply with you",
    guided: [
      "Founder–problem fit explained",
      "Emotional pull vs market hype",
      "Signals you’re on the right path",
    ],
  },
  5: {
    title: "Understand Market Reality",
    focus: "Validate whether the problem truly matters at scale",
    guided: [
      "Why many real problems don’t become businesses",
      "Who suffers vs who pays",
      "Timing and urgency signals",
    ],
  },
  6: {
    title: "Guided Learning Day",
    focus: "Strengthen your foundation through learning",
    guided: [
      "User conversations without pitching",
      "Validating assumptions safely",
      "Early founder mistakes to avoid",
    ],
  },
  7: {
    title: "Reflection & Next Path",
    focus: "Decide how seriously you want to pursue this journey",
    guided: [
      "When to build vs when to learn",
      "Is this worth 3–5 years of your life?",
      "Transitioning from learner to builder",
    ],
  },
};

/* ======================================================
   COMPONENT
====================================================== */
const LiteDayLayout = ({
  day,
  children,
  allowAction = false,
  onComplete,
}) => {
  const { currentDay, completedDays } = useLite();
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  const meta = DAY_META[day];
  const hasCompletedThisDay = completedDays.includes(day);

  // 🔑 Correct, non-ambiguous day states
  const isFutureDay = day > currentDay;
  const isActiveDay = day === currentDay && !hasCompletedThisDay;
  const isPastDay = day < currentDay || hasCompletedThisDay;

  /* --------------------------------------------------
     🔍 DEBUG — SAFE TO KEEP
  -------------------------------------------------- */
  useEffect(() => {
    console.log("[LiteDayLayout] Render", {
      day,
      currentDay,
      completedDays,
      hasCompletedThisDay,
      isActiveDay,
      isPastDay,
      isFutureDay,
      allowAction,
      hasOnComplete: typeof onComplete === "function",
    });
  }, [
    day,
    currentDay,
    completedDays,
    hasCompletedThisDay,
    isActiveDay,
    isPastDay,
    isFutureDay,
    allowAction,
    onComplete,
  ]);

  if (!meta) {
    return (
      <Box sx={{ p: 4 }}>
        <Typography color="error">
          Invalid day configuration.
        </Typography>
      </Box>
    );
  }

  /* --------------------------------------------------
     🔒 FUTURE / LOCKED DAY
  -------------------------------------------------- */
  if (isFutureDay) {
    return (
      <Box sx={{ maxWidth: 900, mx: "auto", p: 4 }}>
        <Card>
          <CardContent>
            <Stack spacing={3}>
              <Chip label={`Day ${day} • Locked`} variant="outlined" />

              <Typography variant="h5" fontWeight="bold">
                {meta.title}
              </Typography>

              <Typography color="text.secondary">
                Complete Day {currentDay} to unlock this step.
              </Typography>

              <Typography sx={{ fontStyle: "italic" }}>
                {meta.focus}
              </Typography>

              <Stack spacing={1}>
                {meta.guided.map((g, idx) => (
                  <Typography
                    key={idx}
                    variant="body2"
                    color="text.secondary"
                  >
                    • {g}
                  </Typography>
                ))}
              </Stack>
            </Stack>
          </CardContent>
        </Card>
      </Box>
    );
  }

  /* --------------------------------------------------
     ✅ ACTIVE / PAST DAY VIEW
  -------------------------------------------------- */
  return (
    <Box sx={{ maxWidth: 1100, mx: "auto", p: 4 }}>
      {/* ---------- HEADER ---------- */}
      <motion.div
        initial={{ opacity: 0, y: -10 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Box
  sx={{
    display: "flex",
    justifyContent: "flex-end",
    alignItems: "center",
    gap: 2,
    mb: 2,
  }}
>
  {user && (
    <>
      <Typography variant="body2" color="text.secondary">
        Logged in as <strong>{user.email}</strong>
      </Typography>

      <Button
        size="small"
        color="inherit"
        onClick={() => {
          // ✅ clear ALL Lite transient state FIRST
          localStorage.removeItem("lite_entry_decided");
          localStorage.removeItem("lite_journey_guest");
          localStorage.removeItem("lite_resume_step");

          // ✅ logout auth
          logout();

          // ✅ HARD redirect LAST
          window.location.replace("/lite/welcome");
        }}
      >
        Logout
      </Button>
    </>
  )}
</Box>
        <Stack spacing={1.5} sx={{ mb: 4 }} alignItems="center">
          {/* 🔰 LOGO (NO TM) */}
          <img
            src="/assets/logo/mdih-no-tm.png"
            alt="MDIH"
            style={{ height: 60 }}
          />

          <Chip
            label={`Day ${day} of 7`}
            color={isPastDay ? "success" : "primary"}
            variant={isPastDay ? "filled" : "outlined"}
          />

          <Typography variant="h4" fontWeight="bold">
            {meta.title}
          </Typography>

          <Typography color="text.secondary">
            {meta.focus}
          </Typography>
        </Stack>
      </motion.div>

      {/* ---------- MAIN CONTENT ---------- */}
      {children}

      {/* --------------------------------------------------
         ✅ COMPLETE DAY CTA
         🔑 ONLY FOR ACTIVE + NOT YET COMPLETED DAY
      -------------------------------------------------- */}
      {isActiveDay &&
        allowAction &&
        typeof onComplete === "function" && (
          <Box textAlign="center" sx={{ mt: 6 }}>
            <Button
              variant="contained"
              color="success"
              size="large"
              sx={{ px: 6, py: 2, fontWeight: "bold" }}
              onClick={onComplete}
            >
              ✅ Complete Day {day}
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ mt: 1, display: "block" }}
            >
              This locks today and unlocks the next step.
            </Typography>
          </Box>
        )}

      {/* --------------------------------------------------
         ➡️ CONTINUE CTA
         🔑 ALWAYS FOR COMPLETED DAYS
      -------------------------------------------------- */}
      {isPastDay && day < 7 && (
        <Box textAlign="center" sx={{ mt: 6 }}>
          <Button
            variant="outlined"
            size="large"
            onClick={() => {
              window.location.href = `/lite/day-${day + 1}`;
            }}
          >
            Continue to Day {day + 1} →
          </Button>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ mt: 1, display: "block" }}
          >
            You’ve already completed this step.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default LiteDayLayout;
